import {Injectable} from '@angular/core';
import {environment} from '../../environments/environment';
import {HttpClient} from '@angular/common/http';
import {JwtService} from './user/jwt.service';
import {interval, map, Observable, switchMap} from 'rxjs';
import {NavigationService} from './nav/navigation.service';
import {logUtishaDebug} from '../util/log';

const { version } = require('../../../package.json');

const VERSION_CHECK_INTERVAL = 1000 * 60 * 5; // 5 minutes
const LOCAL_STORAGE_VERSION_KEY = 'app-version';

@Injectable({
  providedIn: 'root'
})
export class VersionService {

  private frontendVersion = version;

  constructor(
    private http: HttpClient,
    private jwt: JwtService,
    private nav: NavigationService) {
    this.initVersionCheck();
  }

  public getFrontendVersion(): string {
    return this.frontendVersion;
  }

  public getBackendVersion(): Promise<string> {
    return new Promise(resolve => {
      this.http.get(environment.apiUrl + '/actuator/info', {
        headers: {Authorization: 'Bearer ' + this.jwt.token}
      }).subscribe(
        (info: any) => resolve(info.app.version)
      );
    });
  }

  /**
   * Initialize the version checking process
   */
  initVersionCheck(): void {
    this.checkVersion().subscribe();

    // Set up periodic checks
    interval(VERSION_CHECK_INTERVAL)
      .pipe(
        switchMap(() => this.checkVersion())
      )
      .subscribe();
  }

  /**
   * Check the current version against the deployed version by reading index.html
   */
  checkVersion(): Observable<boolean> {
    // Fetch the index.html content as text to check for version meta tag
    // Add cache-busting query parameter to ensure we get the latest version
    return this.http.get('/index.html', {
      responseType: 'text',
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
      params: { '_': Date.now().toString() } // Cache busting
    }).pipe(
      map(htmlContent => {
        // Parse the HTML content to extract the version from meta tag
        const versionMatch = htmlContent.match(/<meta name="app-version" content="([^"]+)">/);
        const deployedVersion = versionMatch ? versionMatch[1] : null;

        if (!deployedVersion) {
          logUtishaDebug(() => 'No version meta tag found in index.html');
          return false;
        }

        const currentVersion = localStorage.getItem(LOCAL_STORAGE_VERSION_KEY);
        logUtishaDebug(() => `Checking version. Current version: ${currentVersion}, deployed version: ${deployedVersion}`);

        // First time the app runs, just store the version
        if (!currentVersion) {
          localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, deployedVersion);
          return false;
        }

        if (currentVersion !== deployedVersion) {
          if (this.nav.isNavigationAllowed()) {
            localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, deployedVersion);
            window.location.reload();
          } // if not, then try again next interval
          return true;
        }

        return false;
      })
    );
  }
}
